import type { ApiHttpClient } from '../client/http.client'
import type { Spoon } from '../client/spoon.client'
import type {
  CreateLiveOptions,
  PageRequestOptions,
  PopularRequestOptions
} from '../interfaces/live.interface'
import { ContentInfo } from '../struct/info.struct'
import { Live, LiveToken, LiveUrl, SoriPublish } from '../struct/live.struct'
import { HttpResponse } from '../struct/response.struct'
import type { InventoryItem } from '../struct/store.struct'
import { User, UserMemberProfile } from '../struct/user.struct'

export class LiveApi {
  constructor(
    private http: ApiHttpClient,
    private spoon: Spoon
  ) {}

  getBanner() {
    return this.http.request(
      '/lives/banner/',
      {
        method: 'GET'
      },
      ContentInfo
    )
  }

  getPopular(options: PopularRequestOptions) {
    return this.http.request(
      '/lives/popular/',
      {
        method: 'GET',
        params: options
      },
      Live
    )
  }

  getSubscribed(options: PageRequestOptions) {
    return this.http.request(
      '/lives/subscribed/',
      {
        method: 'GET',
        params: options
      },
      Live
    )
  }

  getInfo(id: number) {
    return this.http.request(
      `/lives/${id}/`,
      {
        method: 'GET'
      },
      Live
    )
  }

  getLiveToken(id: number) {
    return this.http.request(
      `/lives/${id}/token/`,
      {
        method: 'POST',
        body: {
          device_unique_id: this.spoon.deviceUniqueId
        }
      },
      LiveToken
    )
  }

  createLive(options: CreateLiveOptions) {
    return this.http.request(
      '/lives/',
      {
        method: 'POST',
        body: options
      },
      Live
    )
  }

  soriPublish(live: Live, protocol: 'srt' | 'rtmp' = 'srt') {
    return this.http.httpClient.request(
      `http://${live.host_address}:5021/sori/4/publish/${live.stream_name}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.spoon.token || this.spoon.logonUser.token}`,
          'x-live-authorization': live.jwt
        },
        body: JSON.stringify({
          media: {
            type: 'audio',
            protocol,
            format: 'aac'
          },
          reason: {
            code: 50000,
            message: 'unknown'
          },
          props: {
            country: this.spoon.country,
            stage: 'prod',
            live_id: live.id.toString(),
            user_id: this.spoon.logonUser.id.toString(),
            user_tag: this.spoon.logonUser.tag,
            platform: 'SOPIA',
            os: 'windows'
          }
        })
      },
      SoriPublish
    )
  }

  getLiveUrl() {
    return this.http.request(
      '/commons/live/url/',
      {
        method: 'GET'
      },
      LiveUrl
    )
  }

  close(live: Live, isSave = false) {
    return this.http.request(
      `/lives/${live.id}/close/`,
      {
        method: 'POST',
        body: {
          is_save: isSave
        }
      },
      HttpResponse
    )
  }

  useItem(live: Live, item: InventoryItem, combo = 1, target_id?: number) {
    return this.http.request(
      `/lives/${live.id}/item/${item.id}/`,
      {
        method: 'POST',
        body: {
          combo,
          ...(target_id ? { target_id } : {})
        }
      },
      HttpResponse
    )
  }

  getMemberProfile(live: Live, user: User) {
    return this.http.request(
      `/lives/${live.id}/member/${user.id}/profile/`,
      {
        method: 'GET'
      },
      UserMemberProfile
    )
  }

  block(live: Live, user: User) {
    return this.http.request(
      `/lives/${live.id}/block/`,
      {
        method: 'POST',
        body: {
          block_user_id: user.id
        }
      },
      User
    )
  }

  setManagerList(live: Live, user: User[] = []) {
    return this.http.request(
      `/lives/${live.id}/manager/`,
      {
        method: 'POST',
        body: {
          manager_ids: user.map((u) => u.id)
        }
      },
      User
    )
  }

  updateLive(live: Live) {
    return this.http.request(
      `/lives/${live.id}/`,
      {
        method: 'PUT',
        body: live
      },
      Live
    )
  }
}
